import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { View } from '@adornis/data-view/db/view.js';
import { CampusUserGroup } from '@chancenwerk/db/CampusUserGroup.js';
import { GroupType } from '@chancenwerk/db/enums/enums.js';
import { CampusUserGroupFilter } from '@chancenwerk/db/filter/CampusUserGroupFilter.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-image.js';
import { CrPage } from '@chancenwerk/design/components/cr-page.js';
import '@chancenwerk/design/components/fonts.js';
import { html, type PropertyValueMap } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '../components/campus-component-eventbox.js';
import '../components/campus-component-section-lesson-list.js';
import { CampusRoutes } from '../routes.js';

/**
 * @customElement campus-page-administration-groups
 */
@customElement('campus-page-administration-groups')
export class CampusPageAdministrationGroups extends CrPage {
  @state() private readonly _filter = new CampusUserGroupFilter({});

  @state() private readonly _groupTypeSelected = new RXController<number>(this, 0);

  private readonly _columnDefinitions = [
    {
      columnName: 'Schulkürzel',
      fieldName: 'kuerzel',
      columnWidth: 'max-content',
    },
    {
      columnName: 'Name',
      fieldName: 'name',
      getFieldMapping(entityInstance) {
        return entityInstance.name ?? '';
      },
    },
    {
      columnName: 'Teilnehmer:innen',
      fieldName: 'userIDs',
      getFieldMapping(entityInstance) {
        return entityInstance.userIDs?.length ?? 0;
      },
    },
    {
      columnName: 'Aufgaben',
      fieldName: 'assignedLessonsIDs',
      getFieldMapping(entityInstance) {
        return entityInstance.assignedLessonsIDs?.length ?? 0;
      },
    },
    {
      columnName: 'Events',
      fieldName: 'assignedEventIDs',
      getFieldMapping(entityInstance) {
        return entityInstance.assignedEventIDs?.length ?? 0;
      },
    },
  ] as Array<ColumnDefinition<typeof CampusUserGroup>>;

  override updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);
    if (changedProperties.has('_groupTypeSelected')) {
      this._filter.type = this._groupTypeSelected.value;
    }
  }

  constructor() {
    super();

    this.label = 'Gruppenverwaltung';
    this.description =
      'Hier kannst du deine Lerngruppen verwalten. Kombiniere Schüler:innen und Aufgaben um ihnen personalisiert Aufgaben zuzuweisen.';
    this.permission = ChancenPermission.campus.EDIT_GROUPS;
    this.stickyHeader = true;
  }

  protected override content() {
    return html`
      <cr-flex padding="xs">
        <cr-card space="md">
          <cr-tabs .selected=${this._groupTypeSelected.value}>
            <cr-tab @click=${() => this._groupTypeSelected.next(GroupType.STUDENTS)}> Schüler:innen </cr-tab>
            <cr-tab @click=${() => this._groupTypeSelected.next(GroupType.EMPLOYEES)}> Mitarbeitende </cr-tab>
          </cr-tabs>
          <cr-flex space-between horizontal crossaxis-center space="md">
            <cr-input
              @value-changed=${(e: ValueEvent<string>) => {
                if (this._filter.kuerzel !== e.detail.value) this._filter.kuerzel = e.detail.value;
              }}
              placeholder="Schulkürzel..."
              icon="search"
              flex
            ></cr-input>
            <cr-input
              @value-changed=${(e: ValueEvent<string>) => {
                if (this._filter.search !== e.detail.value) this._filter.search = e.detail.value;
              }}
              placeholder="Name..."
              icon="search"
              flex
            ></cr-input>
            <cr-button icon="add" href="${CampusRoutes.groups + `/edit?groupType=${this._groupTypeSelected.value}`}">
              GRUPPE ERSTELLEN
            </cr-button>
          </cr-flex>
        </cr-card>
      </cr-flex>
      <x-data-view
        .options=${{
          class: CampusUserGroup,
          operation: CampusUserGroup.subscribeGroupsPaginated,
          view: new View<typeof CampusUserGroup>({ filter: this._filter }),
          emptyState: () => this._getEmptyState(),
          selectionSet: CampusUserGroup.allFields,
          isSortable: true,
          onlyShowLastTranslation: true,
          customColumns: this._columnDefinitions,
          rowHref: entity => CampusRoutes.groups + `/edit/${entity._id}?groupType=${this._groupTypeSelected.value}`,
        } satisfies DataViewOptions<typeof CampusUserGroup>}
      ></x-data-view>
    `;
  }

  private _getEmptyState() {
    return html`
      <x-flex padding="xs">
        <cr-text>Es wurden noch keine Gruppen angelegt! </cr-text>
      </x-flex>
    `;
  }
}
